import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

type PolicyKey = string

interface PolicySource {
	title: string
	content: string
}

export class PasswordPolicyModal extends UcoastEl {
	static htmlSelector = 'password-policy-modal'

	titleEl: HTMLElement
	bodyEl: HTMLElement
	panel: HTMLElement
	closeButton: HTMLButtonElement
	sources: Record<PolicyKey, PolicySource> = {}
	triggerEl: HTMLElement | null = null
	isOpen = false
	handleDocClickBound: (e: MouseEvent) => void
	handleKeydownBound: (e: KeyboardEvent) => void

	constructor() {
		super()
		this.titleEl = q.rs('[data-policy-title]', this)
		this.bodyEl = q.rs('[data-policy-body]', this)
		this.panel = q.rs('.password-policy-modal__panel', this)
		this.closeButton = q.rs<HTMLButtonElement>(
			'[data-policy-close-button]',
			this
		)

		this.querySelectorAll<HTMLTemplateElement>(
			'template[data-policy-source]'
		).forEach((tpl) => {
			const key = tpl.getAttribute('data-policy-source') ?? ''
			const titleNode = tpl.content.querySelector('[data-title]')
			const contentNode = tpl.content.querySelector('[data-content]')
			const title = titleNode?.textContent?.trim() ?? ''
			const content = contentNode?.innerHTML?.trim() ?? ''
			if (key) {
				this.sources[key] = { title, content }
			}
		})

		this.handleDocClickBound = this.onPolicyLinkClick.bind(this)
		this.handleKeydownBound = this.onKeydown.bind(this)

		this.querySelectorAll('[data-policy-close]').forEach((el) => {
			el.addEventListener('click', () => this.close())
		})
	}

	connectedCallback() {
		super.connectedCallback()
		document.addEventListener('click', this.handleDocClickBound)
		document.addEventListener('keydown', this.handleKeydownBound)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		document.removeEventListener('click', this.handleDocClickBound)
		document.removeEventListener('keydown', this.handleKeydownBound)
	}

	onPolicyLinkClick(event: MouseEvent) {
		const target = event.target as HTMLElement | null
		if (!target) return
		const link = target.closest('a') as HTMLAnchorElement | null
		if (!link) return
		const href = link.getAttribute('href') ?? ''
		if (!href) return

		const policy = this.resolvePolicyKey(href)
		if (!policy) return
		const source = this.sources[policy]
		if (!source || !source.content) return

		event.preventDefault()
		this.open(policy, link)
	}

	resolvePolicyKey(href: string): PolicyKey | null {
		if (/giveaway/i.test(href)) return 'giveaway'
		if (/privacy[-_]?policy/i.test(href)) return 'privacy'
		if (/terms[-_]?(of[-_]?(service|use))?/i.test(href)) return 'terms'
		return null
	}

	onKeydown(event: KeyboardEvent) {
		if (!this.isOpen) return
		if (event.key === 'Escape') {
			event.preventDefault()
			this.close()
			return
		}
		if (event.key === 'Tab') {
			this.handleTabKey(event)
		}
	}

	handleTabKey(event: KeyboardEvent) {
		const focusable = this.getFocusableElements()
		if (focusable.length === 0) {
			event.preventDefault()
			return
		}
		const first = focusable[0]
		const last = focusable[focusable.length - 1]
		const active = document.activeElement as HTMLElement | null
		const inside = active ? this.panel.contains(active) : false

		if (event.shiftKey) {
			if (!inside || active === first) {
				event.preventDefault()
				last.focus()
			}
		} else {
			if (!inside || active === last) {
				event.preventDefault()
				first.focus()
			}
		}
	}

	getFocusableElements(): HTMLElement[] {
		const selectors =
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
		return Array.from(
			this.panel.querySelectorAll<HTMLElement>(selectors)
		).filter((el) => {
			const rect = el.getBoundingClientRect()
			return rect.width > 0 && rect.height > 0
		})
	}

	open(policy: PolicyKey, trigger: HTMLElement) {
		const source = this.sources[policy]
		if (!source) return

		this.titleEl.textContent = source.title
		this.bodyEl.innerHTML = source.content
		this.triggerEl = trigger
		this.isOpen = true
		this.setAttribute('aria-hidden', 'false')
		document.documentElement.classList.add('password-policy-locked')

		requestAnimationFrame(() => {
			this.closeButton.focus({ preventScroll: true })
		})
	}

	close() {
		if (!this.isOpen) return
		this.isOpen = false
		this.setAttribute('aria-hidden', 'true')
		document.documentElement.classList.remove('password-policy-locked')

		if (this.triggerEl) {
			this.triggerEl.focus({ preventScroll: true })
			this.triggerEl = null
		}
	}
}
